import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Users, Check, ShieldCheck, Compass, ListChecks, CalendarDays } from 'lucide-react';
import planApi, { PlanRole } from '../../services/planApi';
import { StepDef, StepState } from './PlanStepper';
import { useAuth } from '../../contexts/AuthContext';
import ConsentStep from './ConsentStep';
import FodaBoard from './FodaBoard';
import PdiBuilder from './PdiBuilder';
import Availability from './Availability';
import MainLayout from '../layout/MainLayout';
import config from '../../config';
import './plan.scss';

const getImageUrl = (imagePath?: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = config.api.baseUrl.replace(/\/api\/?$/, '');
  return `${baseUrl}/${imagePath}`;
};

type StepKey = 'consent' | 'foda' | 'pdi' | 'agenda';
interface RelItem { relationship_id: number; role: PlanRole; counterpart: string; counterpart_image?: string | null; status?: string; }

const PlanFlow: React.FC<{ only?: 'consent' | 'plan' }> = ({ only }) => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role_name || '');

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const booted = useRef(false);
  const [loading, setLoading] = useState(true);
  const [rels, setRels] = useState<RelItem[]>([]);
  const [rel, setRel] = useState<{ id: number; role: PlanRole; counterpart: string; counterpart_image?: string | null } | null>(null);
  const [plan, setPlan] = useState<any>(null);
  const [active, setActive] = useState<StepKey>('consent');

  const select = useCallback(async (r: RelItem, desiredStep?: StepKey | null) => {
    setRel({ id: r.relationship_id, role: r.role, counterpart: r.counterpart, counterpart_image: r.counterpart_image });
    const p = await planApi.getPlan(r.relationship_id);
    setPlan(p);
    const consentOk = p.locks.fodaEnabled;
    const fodaFinal = p.stages.foda.status === 'finalized';
    const mySigned = r.role === 'mentee' ? !!p.consent?.mentee_signed
      : r.role === 'mentor' ? !!p.consent?.mentor_signed : true;
    const isLocked = (k: StepKey) =>
      k === 'foda' ? !consentOk : k === 'pdi' ? !fodaFinal : k === 'agenda' ? !consentOk : false;
    // Si el usuario aún no firma su propio acuerdo, lo llevamos ahí primero.
    let target: StepKey = !mySigned ? 'consent' : !fodaFinal ? 'foda' : 'pdi';
    // 'agenda' ya no es una sección del plan (las sesiones viven en su propia página).
    if (desiredStep && desiredStep !== 'agenda' && !isLocked(desiredStep)) target = desiredStep;
    // El mentor no tiene FODA/PDI propios: su plan es solo el acuerdo.
    if (r.role === 'mentor') target = 'consent';
    // Páginas independientes: "Acuerdo de Mentoría" (solo consent) y "Mi Plan" (solo foda/pdi).
    if (only === 'consent') target = 'consent';
    else if (only === 'plan') target = !fodaFinal ? 'foda' : 'pdi';
    setActive(target);
  }, [only]);

  // Carga inicial una sola vez (no re-disparar al cambiar la URL con la navegación).
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    (async () => {
      try {
        const data = isAdmin ? await planApi.getAll() : await planApi.getMy();
        const list: RelItem[] = data.relationships || [];
        setRels(list);
        const qs = new URLSearchParams(window.location.search);
        const desired = (qs.get('step') as StepKey) || null;
        const relParam = qs.get('rel');
        const preselect = relParam ? list.find(r => String(r.relationship_id) === relParam) : null;
        if (preselect) await select(preselect, desired);
        else if (list.length === 1) await select(list[0], desired);
      } finally { setLoading(false); }
    })();
  }, [isAdmin, select]);

  // "Acuerdo de Mentoría" y "Mi Plan" son páginas distintas: si se cambia de una
  // a otra, la sección activa se recalcula para no arrastrar la anterior.
  useEffect(() => {
    if (!plan) return;
    if (only === 'consent') setActive('consent');
    else if (only === 'plan') setActive(plan.stages?.foda?.status === 'finalized' ? 'pdi' : 'foda');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [only, plan]);

  // Mantiene la URL (?step=) en sync con la sección activa → al recargar se queda donde estabas.
  useEffect(() => {
    if (!rel) return;
    setSearchParams(prev => {
      if (prev.get('step') === active) return prev;
      const n = new URLSearchParams(prev); n.set('step', active); return n;
    }, { replace: true });
  }, [active, rel, setSearchParams]);

  const refresh = useCallback(async () => {
    if (rel) setPlan(await planApi.getPlan(rel.id));
  }, [rel]);

  if (loading) return <MainLayout><div className="plan"><div className="plan-empty">Cargando…</div></div></MainLayout>;

  if (rels.length === 0) {
    return (
      <MainLayout><div className="plan"><div className="plan-empty">
        <h3>{isAdmin ? 'Sin duplas' : 'Sin dupla asignada'}</h3>
        <p>{isAdmin ? 'Todavía no hay relaciones de mentoría.' : 'Cuando se asigne tu dupla, aquí verás tu plan.'}</p>
      </div></div></MainLayout>
    );
  }

  // Selector de dupla (mentor con varios mentees, doble rol, admin)
  const picker = rels.length > 1 && (
    <div className="dupla-picker">
      <span className="dupla-picker-label"><Users size={15} /> {isAdmin ? 'Dupla' : 'Tus duplas'}:</span>
      {rels.map(r => (
        <button key={r.relationship_id}
          className={`dupla-chip ${rel?.id === r.relationship_id ? 'on' : ''}`}
          onClick={() => select(r)}>
          {r.counterpart}{!isAdmin && <em> · {r.role === 'mentee' ? 'como mentee' : 'como mentor'}</em>}
        </button>
      ))}
    </div>
  );

  if (!rel || !plan) {
    return (
      <MainLayout>
        <div className="plan">
          <div className="plan-head">
            <div className="plan-eyebrow">Plan de mentoría</div>
            <h1>Selecciona una dupla</h1>
          </div>
          {picker}
        </div>
      </MainLayout>
    );
  }

  const consentOk = plan.locks.fodaEnabled;
  const fodaFinal = plan.stages.foda.status === 'finalized';
  const pdiFinal = plan.stages.pdi.status === 'finalized';
  const mySigned = rel.role === 'mentee' ? !!plan.consent?.mentee_signed
    : rel.role === 'mentor' ? !!plan.consent?.mentor_signed : true;

  const stateOf = (k: StepKey): StepState => {
    switch (k) {
      case 'consent': return mySigned ? 'done' : 'active';
      case 'foda': return !consentOk ? 'locked' : fodaFinal ? 'done' : 'active';
      case 'pdi': return !fodaFinal ? 'locked' : pdiFinal ? 'done' : 'active';
      case 'agenda': return !consentOk ? 'locked' : 'todo';
      default: return 'todo';
    }
  };

  // El mentor no tiene FODA/PDI propios (ve los del mentee en "Mi Mentee"); solo firma su acuerdo.
  const stepDefs: Record<string, StepDef> = {
    consent: { key: 'consent', label: 'Acuerdo de Mentoría', state: stateOf('consent') },
    foda: { key: 'foda', label: 'FODA Ejecutivo', state: stateOf('foda') },
    pdi: { key: 'pdi', label: 'Plan de Desarrollo Individual', state: stateOf('pdi') },
  };
  // Páginas independientes: "Acuerdo de Mentoría" (solo consent) y "Mi Plan" (solo foda/pdi).
  // El mentor no tiene FODA/PDI propios (los ve en "Mi Mentee"); su página es solo el acuerdo.
  const steps: StepDef[] =
    only === 'consent' ? [stepDefs.consent]
      : only === 'plan' ? [stepDefs.foda, stepDefs.pdi]
        : rel.role === 'mentor' ? [stepDefs.consent]
          : [stepDefs.consent, stepDefs.foda, stepDefs.pdi];

  const stepIcon: Record<string, React.ReactNode> = {
    consent: <ShieldCheck size={15} />,
    foda: <Compass size={15} />,
    pdi: <ListChecks size={15} />,
    agenda: <CalendarDays size={15} />,
  };

  const go = (k: StepKey) => { if (stateOf(k) !== 'locked') setActive(k); };
  const roleLabel = rel.role === 'mentee' ? 'Tu mentor' : rel.role === 'mentor' ? 'Tu mentee' : 'Dupla';
  const initials = rel.counterpart.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <MainLayout>
    <div className="plan">
      <div className="plan-head">
        <div className="plan-eyebrow">Plan de mentoría {rel.role !== 'mentee' && rel.role !== 'mentor' && '· solo lectura'}</div>
        <h1>{only === 'consent' ? <>Acuerdo de <em>Mentoría</em></>
          : only === 'plan' ? <>Tu Recorrido de <em>Desarrollo</em></>
          : rel.role === 'mentee' ? <>Tu Recorrido de <em>Desarrollo</em></>
          : rel.role === 'mentor' ? <>Acuerdo de <em>Mentoría</em></>
          : <>Recorrido de la <em>Dupla</em></>}</h1>
        <div className="plan-counterpart">
          <span className="plan-counterpart-avatar">
            {getImageUrl(rel.counterpart_image)
              ? <img src={getImageUrl(rel.counterpart_image) as string} alt={rel.counterpart} />
              : <span aria-hidden>{initials}</span>}
          </span>
          <span className="plan-counterpart-info">
            <span className="plan-counterpart-role">{roleLabel}</span>
            <span className="plan-counterpart-name">{rel.counterpart}</span>
          </span>
        </div>
      </div>

      {picker}

      <div className="plan-layout">
        <aside className="plan-nav" aria-label="Secciones del plan">
          {steps.map((s) => {
            const isActive = s.key === active;
            const stateLabel = s.state === 'locked' ? 'Bloqueado'
              : s.state === 'done' ? 'Completado'
              : isActive ? 'En curso' : 'Disponible';
            return (
              <button
                key={s.key}
                type="button"
                className={`plan-nav-item ${isActive ? 'is-active' : ''} state-${s.state}`}
                onClick={() => go(s.key as StepKey)}
                disabled={s.state === 'locked'}
                aria-current={isActive ? 'step' : undefined}
              >
                <span className="plan-nav-num">
                  {s.state === 'locked' ? <Lock size={14} />
                    : s.state === 'done' ? <Check size={16} strokeWidth={3} />
                    : (stepIcon[s.key] || null)}
                </span>
                <span className="plan-nav-text">
                  <span className="plan-nav-label">{s.label}</span>
                  <span className="plan-nav-state">{stateLabel}</span>
                </span>
              </button>
            );
          })}
        </aside>

        <main className="plan-content">
          {only === 'plan' && !consentOk ? (
            <div className="plan-card ac">
              <div className="ac-eyebrow"><ShieldCheck size={13} /> Acuerdo pendiente</div>
              <h2 className="ac-title">Primero firma tu Acuerdo de Mentoría</h2>
              <p className="ac-lead">El FODA y el Plan de Desarrollo Individual se habilitan cuando firmas tu Acuerdo de Mentoría.</p>
              <button className="btn-ink" onClick={() => navigate('/mentee/agreement')}>Ir al Acuerdo de Mentoría</button>
            </div>
          ) : (
            <>
              {active === 'consent' && <ConsentStep relId={rel.id} role={rel.role} consent={plan.consent} counterpart={rel.counterpart} userName={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()} onChanged={refresh} />}
              {active === 'foda' && <FodaBoard relId={rel.id} role={rel.role} counterpart={rel.counterpart} onChanged={refresh} />}
              {active === 'pdi' && <PdiBuilder relId={rel.id} role={rel.role} counterpart={rel.counterpart} onChanged={refresh} />}
              {active === 'agenda' && <Availability relId={rel.id} role={rel.role} consentOk={consentOk} onChanged={refresh} />}
            </>
          )}
        </main>
      </div>
    </div>
    </MainLayout>
  );
};

export default PlanFlow;
